import { Box, Typography, Grid2 } from "@mui/material";
import { Job, JobStatus } from "../../pages/playground/types";
import FormattedText from "../common/formatted-text";

type GroupedJobs = Record<string, Job[]>;

/**
 * Renders a single job card.
 * Extracted to improve readability and maintainability.
 */
function JobCard({
  job,
  onJobClick,
}: {
  job: Job;
  onJobClick: (job: Job) => void;
}) {
  return (
    <Box
      onClick={() => onJobClick(job)}
      sx={{
        border: "1px solid #ccc",
        padding: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        cursor: "pointer",
        transition: "all 0.2s ease",
        position: "relative",
        overflow: "hidden",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 4px 8px -4px rgba(0, 0, 0, 0.4)",
        },
      }}
    >
      {/* Images */}
      <Box sx={{ position: "relative", width: "100%", mb: 0 }}>
        {job.thumbnails && job.thumbnails.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0,
              width: "100%",
              borderRadius: 1,
            }}
          >
            {job.thumbnails.slice(0, 6).map((image, idx) => (
              <Box
                key={idx}
                sx={{
                  width:
                    (job?.thumbnails?.length || 0) === 1
                      ? "100%"
                      : "calc(100% / 3)",
                  maxWidth:
                    (job?.thumbnails?.length || 0) === 1
                      ? "100%"
                      : "calc(100% / 3)",
                }}
              >
                <Box
                  component="img"
                  src={image}
                  alt={`${job.title || "Product"} image ${idx + 1}`}
                  maxHeight={120}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              No images available
            </Typography>
          </Box>
        )}
      </Box>

      <Box>
        {/* Meta information */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Typography variant="body2">{job.master_id}</Typography>

          <Box sx={{ py: 0.5, px: 1, fontSize: "0.75rem", fontWeight: "bold" }}>
            {job.status === JobStatus.DELIVERED && (
              <Typography variant="body2" color="primary.contrastText">
                To be approved
              </Typography>
            )}
            {job.status === JobStatus.COMPLETED && (
              <Typography variant="body2" color="success.main">
                Approved
              </Typography>
            )}
            {job.status === JobStatus.TO_BE_REWORKED && (
              <Typography variant="body2" color="error.main">
                To be reworked
              </Typography>
            )}
          </Box>
        </Box>

        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ mt: 0.5, lineHeight: 1 }}
        >
          {job.title ? <FormattedText text={job.title} /> : "No title"}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Main grid component. Renders either grouped jobs or a flat list.
 */
export default function ClientBatchGrid({
  jobs,
  groupedJobs = null,
  onJobClick,
}: {
  jobs: Job[];
  groupedJobs?: GroupedJobs | null;
  onJobClick: (job: Job) => void;
}) {
  const hasGroups = groupedJobs && Object.keys(groupedJobs).length > 0;

  if (hasGroups && groupedJobs) {
    return (
      <Box>
        {Object.entries(groupedJobs).map(([groupKey, groupJobs]) => (
          <Box key={groupKey} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {groupKey} ({groupJobs.length})
            </Typography>

            <Grid2 container spacing={3}>
              {groupJobs.map((job) => (
                <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={job.id}>
                  <JobCard job={job} onJobClick={onJobClick} />
                </Grid2>
              ))}
            </Grid2>
          </Box>
        ))}
      </Box>
    );
  }

  // Ungrouped view
  return (
    <Grid2 container spacing={3}>
      {jobs.length === 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No SKUs available
          </Typography>
        </Box>
      )}

      {jobs.map((job) => (
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={job.id}>
          <JobCard job={job} onJobClick={onJobClick} />
        </Grid2>
      ))}
    </Grid2>
  );
}
